import authcheck from '@/helpers/authcheck'
import { API, handleError, hapiDomain } from '@/helpers/axios'
import generateUuid from '@/helpers/uuid'
import { eventHub } from '@/store/eventHub'
import { UserSession } from '@/store/auth/types'

let socket: WebSocket | null = null
const basePath = '/wss/'
let hasSentHello = false
let reconnectTimeout: number | null = null
const messageQueue: WssSendObject[] = []

interface WssSendObject {
  type: string;
  id?: string | null;
  version: string;
  auth?: string | Record<string, unknown> | null;
  subs?: string[];
  path?: string;
}

const wsClient = {
  authCheck: async (): Promise<UserSession | null> => {
    return await authcheck.init()
  },
  close: (reason?: string, code = 1000): void => {
    // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
    if(reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }
    if(socket) {
      try {
        socket.close(code, reason)
      } finally {
        socket = null
        hasSentHello = false
      }
    }
  },
  wssAuth: async (): Promise<{data: {authenticated: boolean, status: string}}> => {
    try {
      const wssAuth = await API.get('nes/auth')
      sessionStorage.setItem('wss-token', wssAuth.data.token)
      return wssAuth
    }
    catch(error) { handleError(error) }
    return { data: { authenticated: false, status: 'unauthenticated' } }
  },
  initialize: (userId: number): void => {
    // Ensure we have a client id stored for this browser session
    let websocketClientId = sessionStorage.getItem('websocketId')
    if(websocketClientId === null) {
      const newWebsocketClientId = generateUuid()
      sessionStorage.setItem('websocketId', newWebsocketClientId)
      websocketClientId = newWebsocketClientId
    }

    if(!socket || socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
      socket = new WebSocket('wss://' + hapiDomain() + '/wss')
      hasSentHello = false

      socket.onmessage = wsClient.handleMessage
      socket.onerror = (error) => console.error('websocket error: ', error)
      socket.onopen = (event) => {
        eventHub.$emit('websocketOpen', event)
        // Send hello first on a fresh/opened socket
        wsClient.sendHello(userId, websocketClientId)
        hasSentHello = true
        while(messageQueue.length > 0) {
          const queued = messageQueue.shift()
          if(queued) wsClient.send(queued)
        }
      }
      socket.onclose = (event) => wsClient.reopenSocket(event, userId)
    } else if(socket.readyState === WebSocket.OPEN) {
      // Socket already open; send hello only once per connection
      if(!hasSentHello) {
        wsClient.sendHello(userId, websocketClientId)
        hasSentHello = true
      }
    }
    return
  },
  reopenSocket(event: { reason: string; wasClean: boolean; code: number; }, userId: number) {
    hasSentHello = false
    if(event && event.reason === '') {
      if(reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      reconnectTimeout = window.setTimeout(() => {
        wsClient.initialize(userId)
      }, 1000)
    }
  },
  sendHello(userId: number, websocketClientId?: string | null): void {
    if(!websocketClientId)
      websocketClientId = sessionStorage.getItem('websocketId')
    if(websocketClientId) {
      wsClient.send({
        type: 'hello',
        id: websocketClientId,
        version: '2',
        subs: ['/wss/user/' + userId + '/session']
      })
    }
  },
  handleMessage: (message: { data: string }): void => {
    const msgData = JSON.parse(message.data)
    let eventName = ''
    switch(msgData.type) {
      case 'hello':
        eventHub.$emit('websocketHello')
        break
      case 'ping':
        wsClient.send({
          type: 'ping',
          version: '2'
        })
        break
      case 'sub':
        if(msgData.statusCode === 400 && msgData.payload.message === 'Connection is not initialized') {
          wsClient.subscribe(msgData.path)
        }
        break
      case 'pub':
        eventName = `websocketPub-${msgData.path}`.replace('/', '-')
        eventHub.$emit(eventName, msgData.message.message)
        break
      default:
        // TODO: Handle message indicating user is unauthenticated
        // UPDATE: Not sure what triggers that message anymore. Changed to a console.error to spot it more easily
        console.error('we received a ' + msgData.type + ' message from Hapi WSS', msgData)
        break
    }
  },
  send: (message: WssSendObject): void => {
    if(!message.auth && sessionStorage.getItem('wss-token'))
      message.auth = sessionStorage.getItem('wss-token')
    if(!message.id && sessionStorage.getItem('websocketId'))
      message.id = sessionStorage.getItem('websocketId')
    if(socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message))
      return
    }
    // Queue messages until the socket is open
    messageQueue.push(message)
  },
  subscribe: (path: string): void => {
    const statusCheck = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        clearInterval(statusCheck)
        wsClient.send({
          type: 'sub',
          path: basePath + path,
          version: '2'
        })
      } else {
        // console.log('readyState', socket?.readyState)
      }
    }, 1000)
  }
}

export default wsClient
